import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { identarPlantilla, identarJson } from 'src/app/utils/identar';

/**
 * PARÁMETROS → INTEGRACIONES BANCARIAS (Mario, 09-oct; INT-01 a 05).
 * El formato de cada banco es configuración, no código: la plantilla del archivo de envío (formsheets) y, para la
 * respuesta, el JSON de lectura + qué campo es cada dato + la tabla de códigos del banco. Cada cambio es una versión
 * nueva que rige solo después de una prueba en verde con archivo de ejemplo. La cambian la gerente o el director
 * (uno basta), con motivo. MANUAL (el Excel del lote) es interna: siempre encendida. Domiciliación: después.
 */
type Codigo = { valor: string; resultado: 'PAGADO' | 'NO_PROCESADO' | 'SIN_PROCESAR'; motivo?: string };

@Component({
  selector: 'app-integraciones',
  templateUrl: './integraciones.component.html'
})
export class IntegracionesComponent implements OnInit {
  sentido: 'PAGO' | 'DOMICILIACION' = 'PAGO';
  cargando = true;
  ocupado = false;
  error = '';
  aviso = '';
  lista: any[] = [];
  campos: any = { lote: [], pago: [] };
  motivos: string[] = [];
  bancos: { bankCode: string; name: string }[] = [];
  custom: { envio: any[]; respuesta: any[] } = { envio: [], respuesta: [] };

  // ficha
  codigo: string | null = null;
  ficha: any = null;
  n = 0;                        // la versión que se mira
  editando = false;
  creando = false;
  form: any = null;
  configTexto = '';
  motivo = '';
  archivo: File | null = null;
  resultado: any = null;

  readonly RESULTADOS = [{ v: 'PAGADO', t: 'Pagado' }, { v: 'NO_PROCESADO', t: 'No procesado' }, { v: 'SIN_PROCESAR', t: 'Sin procesar (sigue pendiente)' }];
  readonly ACCION: any = { CREAR: 'Creada', CAMBIAR_FORMATO: 'Versión nueva', PROBAR: 'Prueba', PUBLICAR: 'Publicada', ENCENDER: 'Encendida', APAGAR: 'Apagada' };

  constructor(private liq: LiquidacionService, public _auth: AuthService, private ruta: ActivatedRoute, private router: Router) { }

  get puedeCambiar(): boolean { return this._auth.hasPermission('liquidacion.aprobar'); }
  /** INT-03: la acción custom de respuesta de esta versión arma su propia respuesta de muestra (no hace falta subir nada). */
  get respuestaConMuestra(): boolean {
    const r = this.version && this.version.formato && this.version.formato.respuesta;
    return !!(r && r.lector === 'CUSTOM' && (this.custom.respuesta || []).some((x: any) => x.code === r.funcion && x.muestra));
  }
  get version(): any { return this.ficha ? (this.ficha.versiones || []).find((v: any) => v.n === this.n) : null; }
  verde = (v: any): boolean => !!(v && v.prueba && v.prueba.envio_ok && v.prueba.lectura_ok);

  ngOnInit(): void {
    this.ruta.queryParamMap.subscribe((q) => {
      this.codigo = q.get('integracion');
      this.limpiar();
      this.cargar();
    });
  }

  private limpiar(): void { this.editando = false; this.creando = false; this.form = null; this.motivo = ''; this.archivo = null; this.resultado = null; this.error = ''; }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try {
      const r = await this.liq.integraciones(this.sentido);
      this.lista = r?.integraciones || []; this.campos = r?.campos_plantilla || this.campos; this.motivos = r?.motivos || [];
      this.bancos = r?.bancos || []; this.custom = r?.custom || { envio: [], respuesta: [] };
      this.ficha = this.codigo ? this.lista.find((i) => i.code === this.codigo) || null : null;
      if (this.ficha) this.n = this.n && this.ficha.versiones.some((v: any) => v.n === this.n) ? this.n : (this.ficha.vigente || Math.max(...this.ficha.versiones.map((v: any) => v.n)));
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudieron leer las integraciones.'; }
    finally { this.cargando = false; }
  };

  verSentido = (s: 'PAGO' | 'DOMICILIACION'): void => { this.sentido = s; this.abrir(null); this.cargar(); };
  abrir = (code: string | null): void => { this.n = 0; this.router.navigate([], { queryParams: { integracion: code }, queryParamsHandling: 'merge' }); };
  verVersion = (n: number): void => { this.n = n; this.resultado = null; this.archivo = null; this.editando = false; };

  // ── editor del formato (crear o versión nueva) ──
  private formatoVacio(): any {
    return { envio: { tipo: 'PLANTILLA', funcion: '', archivo: 'TXT', nombre_archivo: '{lote}.txt', plantilla: '' },
      respuesta: { lector: 'TXT', funcion: '', config: [], campo_referencia: '', campo_estado: '', campo_referencia_bancaria: '', campo_motivo: '', registro_detalle: null,
        codigos: [{ valor: '', resultado: 'PAGADO' }, { valor: '*', resultado: 'NO_PROCESADO', motivo: '' }] as Codigo[] } };
  }
  empezarNueva = (): void => {
    this.limpiar(); this.creando = true;
    this.form = { code: '', nombre: '', banco_codigo: '', ...this.formatoVacio() };
    this.configTexto = '[]';
  };
  editar = (): void => {
    const f = JSON.parse(JSON.stringify(this.version.formato));
    f.respuesta.registro_detalle = f.respuesta.registro_detalle || null;
    f.envio.tipo = f.envio.tipo || 'PLANTILLA'; f.envio.funcion = f.envio.funcion || ''; f.respuesta.funcion = f.respuesta.funcion || '';
    if (f.envio.plantilla) f.envio.plantilla = identarPlantilla(f.envio.plantilla);
    this.form = f; this.configTexto = JSON.stringify(f.respuesta.config || [], null, 2); this.editando = true; this.motivo = ''; this.resultado = null;
  };
  cancelar = (): void => { this.editando = false; this.creando = false; this.form = null; this.motivo = ''; };
  agregarCodigo = (): void => { this.form.respuesta.codigos.push({ valor: '', resultado: 'NO_PROCESADO', motivo: '' }); };
  quitarCodigo = (k: number): void => { this.form.respuesta.codigos.splice(k, 1); };
  get camposLector(): string[] {
    try { return [...new Set((JSON.parse(this.configTexto) || []).flatMap((r: any) => (r.attr || []).map((a: any) => a.name)))] as string[]; } catch (e) { return []; }
  }

  // ── identar (Mario, 09-oct noche): solo cambia cómo se ve; el archivo es el mismo ──
  identarPlantillaForm = (): void => { this.form.envio.plantilla = identarPlantilla(this.form.envio.plantilla); };
  identarLector = (): void => { try { this.configTexto = identarJson(this.configTexto); this.error = ''; } catch (e: any) { this.error = e.message; } };
  verPlantilla = (t: string): string => identarPlantilla(t);
  verJson = (c: any): string => { try { return JSON.stringify(typeof c === 'string' ? JSON.parse(c) : c, null, 2); } catch (e) { return String(c); } };
  nombreBanco = (code: string): string => { const b = this.bancos.find((x) => x.bankCode === code); return b ? `${b.name} (${code})` : code || 'Cualquiera'; };

  private formatoDelForm(): any {
    let config: any;
    const r = this.form.respuesta;
    if (r.lector !== 'CUSTOM') { try { config = JSON.parse(this.configTexto); } catch (e) { throw new Error('El formato del lector no es un JSON válido.'); } }
    const codigos = r.codigos.filter((c: Codigo) => String(c.valor).trim()).map((c: Codigo) => ({ valor: String(c.valor).trim(), resultado: c.resultado, ...(c.resultado === 'NO_PROCESADO' ? { motivo: c.motivo || '' } : {}) }));
    const e = this.form.envio;
    const envio = e.tipo === 'CUSTOM' ? { tipo: 'CUSTOM', funcion: e.funcion, nombre_archivo: e.nombre_archivo } : { tipo: 'PLANTILLA', archivo: e.archivo, nombre_archivo: e.nombre_archivo, plantilla: e.plantilla };
    return { envio, respuesta: { lector: r.lector, ...(r.lector === 'CUSTOM' ? { funcion: r.funcion } : { config }), campo_referencia: r.campo_referencia, campo_estado: r.campo_estado,
      campo_referencia_bancaria: r.campo_referencia_bancaria, ...(r.campo_motivo ? { campo_motivo: r.campo_motivo } : {}),
      ...(r.registro_detalle && r.registro_detalle.campo ? { registro_detalle: r.registro_detalle } : {}), codigos } };
  }

  guardar = async (): Promise<void> => {
    if (!this.motivo.trim()) { this.error = 'Todo cambio lleva motivo.'; return; }
    let formato: any;
    try { formato = this.formatoDelForm(); } catch (e: any) { this.error = e.message; return; }
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      if (this.creando) {
        const i = await this.liq.crearIntegracion({ code: String(this.form.code).trim().toUpperCase(), nombre: this.form.nombre, banco_codigo: this.form.banco_codigo || null, sentido: this.sentido, formato, motivo: this.motivo.trim() });
        this.aviso = `Integración ${i.code} creada (apagada). Pruébala con un archivo de ejemplo antes de encenderla.`;
        this.cancelar(); this.abrir(i.code);
      } else {
        const i = await this.liq.nuevaVersionIntegracion(this.ficha.code, formato, this.motivo.trim());
        const n = Math.max(...i.versiones.map((v: any) => v.n));
        this.aviso = `Versión ${n} guardada. No rige hasta probarla en verde y publicarla.`;
        this.cancelar(); this.n = n; await this.cargar();
      }
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo guardar.'; }
    finally { this.ocupado = false; }
  };

  // ── prueba (INT-03) ──
  elegirArchivo = (ev: Event): void => { const f = (ev.target as HTMLInputElement).files; this.archivo = f && f.length ? f[0] : null; };
  probar = async (): Promise<void> => {
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      this.resultado = await this.liq.probarIntegracion(this.ficha.code, this.n, this.archivo);
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo probar.'; }
    finally { this.ocupado = false; }
  };

  accion = async (accion: 'publicar' | 'encender' | 'apagar'): Promise<void> => {
    if (!this.motivo.trim()) { this.error = 'Escribe el motivo.'; return; }
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      await this.liq.accionIntegracion(this.ficha.code, accion, { n: this.n, motivo: this.motivo.trim() });
      this.aviso = accion === 'publicar' ? `La versión ${this.n} rige desde ahora para los lotes nuevos.` : accion === 'encender' ? `${this.ficha.code} encendida: el tesorero ya la puede elegir al armar un lote.` : `${this.ficha.code} apagada: no se arman lotes nuevos con ella; los que están en curso terminan con su versión.`;
      this.motivo = ''; await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo.'; }
    finally { this.ocupado = false; }
  };

  fecha = (iso: any): string => {
    if (!iso) return '';
    const d = new Date(new Date(iso).getTime() - 4 * 3600e3).toISOString();
    return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)} ${d.slice(11, 16)}`;
  };
  resultadoTexto = (r: string): string => ({ PAGADO: 'Pagado', NO_PROCESADO: 'No procesado', SIN_PROCESAR: 'Sin procesar' } as any)[r] || 'No se reconoce';
  resultadoClase = (r: string): string => ({ PAGADO: 'kb-badge-exito', NO_PROCESADO: 'kb-badge-peligro', SIN_PROCESAR: 'kb-badge-neutro' } as any)[r] || 'kb-badge-alerta';
}
